import { Tr } from '@chakra-ui/react'
import React, { useState, useCallback } from 'react'
import { Column, RowData, TableType } from './MasterTable'
import TableCell from './TableCell'

interface TableRowProps {
  columns: Column[]
  row: RowData
  onRowClick?: (row: RowData) => void
  onSearch?: (row: RowData) => void
  onOpenNewTab?: (row: RowData) => void
  onRowIntent?: (row: RowData) => void
  /** Whether this row is selected. Passed as a boolean (not the full selection
   * array) so memoized rows only re-render when their own state changes. */
  isSelected: boolean
  onSelectRow: (id: number | string, isChecked: boolean) => void
  tableType: TableType
}

const TableRowItem: React.FC<TableRowProps> = React.memo(({
  columns,
  row,
  onRowClick,
  onOpenNewTab,
  onSearch,
  onRowIntent,
  isSelected,
  onSelectRow,
  tableType,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleRowClick = useCallback(() => {
    onRowClick?.(row)
  }, [onRowClick, row])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
    onRowIntent?.(row)
  }, [onRowIntent, row])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const handleFocus = useCallback(() => {
    onRowIntent?.(row)
  }, [onRowIntent, row])

  const handlePointerDown = useCallback(() => {
    onRowIntent?.(row)
  }, [onRowIntent, row])

  if (!row) {
    return null
  }

  return (
    <Tr
      onClick={handleRowClick}
      cursor={onRowClick ? 'pointer' : 'auto'}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onPointerDown={handlePointerDown}
      onMouseLeave={handleMouseLeave}
      backgroundColor={isHovered ? 'solid.25' : 'transparent'}
      py="0px"
    >
      {columns.map((column) => (
        <TableCell
          key={column.accessor}
          column={column}
          value={row[column.accessor]}
          row={row}
          onOpenNewTab={onOpenNewTab}
          onSearch={onSearch}
          isSelected={isSelected}
          onSelectRow={onSelectRow}
          tableType={tableType}
          isHovered={isHovered}
        />
      ))}
    </Tr>
  )
})

TableRowItem.displayName = 'TableRowItem'

export default TableRowItem
